<template>
  <div id="views-landing" class="analysis-page">
    <!-- spinner section -->
    <div class="loading" v-if="loading">
      <div class="loading__content">
        <span>Analysing data...</span>
        <b-spinner variant="success" label="Spinning" class="spinner"> </b-spinner>
      </div>
    </div>

    <section class="analysis-hero">
      <div class="analysis-hero__copy">
        <span class="analysis-hero__eyebrow">Insight Workspace</span>
        <h1>Health inventory analysis at a glance.</h1>
        <p>
          Review project and dataset activity, monitor delivery status, and filter submissions by
          month and year from one clear, responsive overview.
        </p>
      </div>

      <div class="analysis-hero__summary">
        <div class="hero-pill">
          <span class="hero-pill__label">Projects tracked</span>
          <strong>{{ projects.length }}</strong>
        </div>
        <div class="hero-pill">
          <span class="hero-pill__label">Datasets tracked</span>
          <strong>{{ datasets.length }}</strong>
        </div>
      </div>
    </section>

    <section class="filter-shell">
      <article class="filter-card">
        <div class="filter-card__header">
          <div>
            <span class="filter-card__eyebrow">Project filters</span>
            <h2>Filter projects by month and year</h2>
          </div>
          <b-button v-b-toggle.collapse-1 variant="primary" class="filter-card__toggle">
            Toggle filters
          </b-button>
        </div>

        <b-collapse id="collapse-1" visible class="filter-card__collapse">
          <div class="filter-grid">
            <div class="filter-field">
              <label>Year</label>
              <b-form-select v-model="projectYear" :options="years"></b-form-select>
            </div>
            <div class="filter-field">
              <label>Month</label>
              <b-form-select v-model="projectMonth" :options="months"></b-form-select>
            </div>
            <div class="filter-total">
              <span class="filter-total__label">Matching projects</span>
              <strong class="value">{{ filteredProjects.length }}</strong>
            </div>
          </div>
        </b-collapse>
      </article>

      <article class="filter-card">
        <div class="filter-card__header">
          <div>
            <span class="filter-card__eyebrow">Dataset filters</span>
            <h2>Filter datasets by month and year</h2>
          </div>
          <b-button v-b-toggle.collapse-2 variant="primary" class="filter-card__toggle">
            Toggle filters
          </b-button>
        </div>

        <b-collapse id="collapse-2" visible class="filter-card__collapse">
          <div class="filter-grid">
            <div class="filter-field">
              <label>Year</label>
              <b-form-select v-model="datasetYear" :options="years"></b-form-select>
            </div>
            <div class="filter-field">
              <label>Month</label>
              <b-form-select v-model="datasetMonth" :options="months"></b-form-select>
            </div>
            <div class="filter-total">
              <span class="filter-total__label">Matching datasets</span>
              <strong class="value">{{ filteredDatasets.length }}</strong>
            </div>
          </div>
        </b-collapse>
      </article>
    </section>

    <!-- card section -->
    <section id="card-section" class="container analysis-section">
      <div class="section-heading">
        <div>
          <span class="section-heading__eyebrow">Project analysis</span>
          <h3 class="header">Project performance overview</h3>
        </div>
      </div>

      <div class="row text-center analysis-grid">
        <div class="col-12 col-md-6 col-xl-4 mb-4">
          <Cards to="#">
            <template v-slot:image>
              <img
                src="../../../assets/ngoimgs/book.png"
                class="img-fluid analysis-icon"
                width="50"
              />
            </template>
            <template v-slot:paragraph> Total Projects </template>
            <template v-slot:subtitle>
              <span class="value">
                {{ projects.length }}
              </span>
            </template>
            <span>{{ projects.length }}</span>
          </Cards>
        </div>
        <div class="col-12 col-md-6 col-xl-4 mb-4">
          <Cards to="#">
            <template v-slot:image>
              <img
                src="../../../assets/ngoimgs/book.png"
                class="img-fluid analysis-icon"
                width="50"
              />
            </template>
            <template v-slot:paragraph> Completed projects </template>
            <template v-slot:subtitle>
              <span class="value">
                {{ completedProjects.length }}
              </span></template
            >
          </Cards>
        </div>
        <div class="col-12 col-md-6 col-xl-4 mb-4">
          <Cards to="#">
            <template v-slot:image>
              <img
                src="../../../assets/ngoimgs/book.png"
                class="img-fluid analysis-icon"
                width="50"
              />
            </template>
            <template v-slot:paragraph> Ongoing Projects </template>
            <template v-slot:subtitle>
              <span class="value">
                {{ ongoingProjects.length }}
              </span>
            </template>
          </Cards>
        </div>
      </div>
    </section>

    <div class="analysis-divider"></div>

    <!-- card section -->
    <section id="card-section" class="container analysis-section">
      <div class="section-heading">
        <div>
          <span class="section-heading__eyebrow">Dataset analysis</span>
          <h3 class="header">Dataset submission overview</h3>
        </div>
      </div>

      <div class="row text-center analysis-grid">
        <div class="col-12 col-md-6 col-xl-4 mb-4">
          <Cards to="#">
            <template v-slot:image>
              <img
                src="../../../assets/ngoimgs/book.png"
                class="img-fluid analysis-icon"
                width="50"
              />
            </template>
            <template v-slot:paragraph> Total Datasets </template>
            <template v-slot:subtitle>
              <span class="value">
                {{ datasets.length }}
              </span>
            </template>
            <!-- <template v-slot:subtitle> Explore and Submit all Health Projects </template> -->
          </Cards>
        </div>
        <div class="col-12 col-md-6 col-xl-4 mb-4">
          <Cards to="#">
            <template v-slot:image>
              <img
                src="../../../assets/ngoimgs/book.png"
                class="img-fluid analysis-icon"
                width="50"
              />
            </template>
            <template v-slot:paragraph> Completed Datasets </template>
            <template v-slot:subtitle>
              <span class="value">
                {{ completedDatasets.length }}
              </span>
            </template>
          </Cards>
        </div>
        <div class="col-12 col-md-6 col-xl-4 mb-4">
          <Cards to="#">
            <template v-slot:image>
              <img
                src="../../../assets/ngoimgs/book.png"
                class="img-fluid analysis-icon"
                width="50"
              />
            </template>
            <template v-slot:paragraph> Pending Datasets </template>
            <template v-slot:subtitle>
              <span class="value">
                {{ pendingDatasets.length }}
              </span>
            </template>
          </Cards>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Cards from './bits/bits.index';

export default {
  name: 'ViewsAnalysis',
  components: {
    Cards,
  },
  data() {
    return {
      projects: [],
      datasets: [],
      completedProjects: [],
      ongoingProjects: [],
      januaryProjects: [],
      filteredProjects: [],
      filteredDatasets: [],
      completedDatasets: [],
      pendingDatasets: [],
      loading: true,
      months: [
        { value: 1, text: 'January' },
        { value: 2, text: 'February' },
        { value: 3, text: 'March' },
        { value: 4, text: 'April' },
        { value: 5, text: 'May' },
        { value: 6, text: 'June' },
        { value: 7, text: 'July' },
        { value: 8, text: 'August' },
        { value: 9, text: 'September' },
        { value: 10, text: 'October' },
        { value: 11, text: 'November' },
        { value: 12, text: 'December' },
      ],
      years: [
        { value: 2005, text: '2005' },
        { value: 2006, text: '2006' },
        { value: 2007, text: '2007' },
        { value: 2008, text: '2008' },
        { value: 2009, text: '2009' },
        { value: 2010, text: '2010' },
        { value: 2011, text: '2011' },
        { value: 2012, text: '2012' },
        { value: 2013, text: '2013' },
        { value: 2014, text: '2014' },
        { value: 2015, text: '2015' },
        { value: 2016, text: '2016' },
        { value: 2017, text: '2017' },
        { value: 2018, text: '2018' },
        { value: 2019, text: '2019' },
        { value: 2020, text: '2020' },
        { value: 2021, text: '2021' },
        { value: 2022, text: '2022' },
        { value: 2023, text: '2023' },
      ],
      projectYear: 2022,
      projectMonth: 6,
      datasetYear: 2022,
      datasetMonth: 6,
    };
  },
  async mounted() {
    await this.fetchData();
  },

  watch: {
    async projectYear() {
      console.log(this.projectYear, 'project year');
      this.filteredProjects = await this.filterProjectsByMonthYear(
        this.projects,
        this.projectYear,
        this.projectMonth
      );
    },

    async projectMonth() {
      this.filteredProjects = await this.filterProjectsByMonthYear(
        this.projects,
        this.projectYear,
        this.projectMonth
      );
      console.log(this.projectMonth, 'project month');
    },

    async datasetYear() {
      this.filteredDatasets = await this.filterProjectsByMonthYear(
        this.datasets,
        this.datasetYear,
        this.datasetMonth
      );
    },

    async datasetMonth() {
      this.filteredDatasets = await this.filterProjectsByMonthYear(
        this.datasets,
        this.datasetYear,
        this.datasetMonth
      );
    },
  },

  methods: {
    async filterProjectsByStatus(projects, status) {
      return projects.filter(
        (project) => project.status.value.toLowerCase() === status.toLowerCase()
      );
    },

    async filterProjectsByMonthYear(projects, year, month) {
      return projects.filter((project) => {
        const createdAtDate = new Date(project.updated_at);
        const createdAtYear = createdAtDate.getFullYear();
        const createdAtMonth = createdAtDate.getMonth() + 1; // January is 0, so we add 1 to get the correct month.

        return createdAtYear === year && createdAtMonth === month;
      });
    },

    async filterDatasetsByStatus(datasets, status) {
      return datasets.filter(
        (dataset) => dataset.status.value.toLowerCase() === status.toLowerCase()
      );
    },

    async fetchData() {
      this.loading = true;
      try {
        const projects = await this.$dataset.getAllProjects();
        this.projects = projects.data.results;

        this.completedProjects = await this.filterProjectsByStatus(this.projects, 'Completed');
        console.log(this.completedProjects, 'completed');

        // Assuming you want to filter projects created in January 2022
        this.januaryProjects = await this.filterProjectsByMonthYear(this.projects, 2023, 5);

        this.ongoingProjects = await this.filterProjectsByStatus(this.projects, 'Ongoing');
        this.tempProjects = this.projects;
        this.total = projects.data.count;

        // for datasets
        const datasets = await this.$dataset.getDatasets();
        this.datasets = datasets.data.results;
        console.log(this.datasets, 'datasets');

        this.completedDatasets = await this.filterDatasetsByStatus(this.datasets, 'Completed');
        this.pendingDatasets = await this.filterDatasetsByStatus(this.datasets, 'Pending');

        this.filteredProjects = await this.filterProjectsByMonthYear(
          this.projects,
          this.projectYear,
          this.projectMonth
        );
        this.filteredDatasets = await this.filterProjectsByMonthYear(
          this.datasets,
          this.datasetYear,
          this.datasetMonth
        );
        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Raleway&display=swap');

.analysis-page {
  padding: 1.75rem 0 3rem;
  background: radial-gradient(circle at top left, rgba(48, 171, 76, 0.12), transparent 24%),
    linear-gradient(180deg, #f4faf5 0%, #eef5ef 100%);
}

.header {
  margin: 0;
  color: #163322;
  font-family: 'Raleway', sans-serif;
  font-size: clamp(1.6rem, 2.6vw, 2.25rem);
  line-height: 1.15;
}

.loading {
  position: fixed;
  inset: 0;
  background: rgba(10, 42, 18, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(4px);
}

.loading__content {
  display: inline-flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem 1.2rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  color: #173425;
  font-weight: 700;
  box-shadow: 0 20px 40px rgba(15, 38, 23, 0.16);
}

.analysis-hero {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  max-width: 1240px;
  margin: 0 auto 1.75rem;
  padding: 2rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 30px;
  background: linear-gradient(135deg, rgba(10, 33, 17, 0.96), rgba(23, 72, 36, 0.88)),
    linear-gradient(140deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 28px 80px rgba(17, 41, 24, 0.16);
  color: #f7fff7;
}

.analysis-hero__copy {
  max-width: 48rem;
}

.analysis-hero__eyebrow,
.section-heading__eyebrow,
.filter-card__eyebrow,
.filter-total__label {
  display: inline-block;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.analysis-hero__eyebrow {
  color: rgba(247, 255, 247, 0.72);
}

.analysis-hero h1 {
  margin: 0.7rem 0 0.85rem;
  font-family: 'Raleway', sans-serif;
  font-size: clamp(2rem, 3vw, 3rem);
  line-height: 1.05;
  color: #ffffff;
}

.analysis-hero p {
  margin: 0;
  max-width: 40rem;
  color: rgba(247, 255, 247, 0.82);
  line-height: 1.75;
}

.analysis-hero__summary {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  min-width: 18rem;
}

.hero-pill {
  padding: 1rem 1.1rem;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.hero-pill__label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  color: rgba(247, 255, 247, 0.72);
}

.hero-pill strong {
  font-size: 1.25rem;
  color: #ffffff;
}

.filter-shell {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
  max-width: 1240px;
  margin: 0 auto 2rem;
}

.filter-card {
  padding: 1.4rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.84);
  box-shadow: 0 18px 60px rgba(22, 50, 28, 0.08);
  backdrop-filter: blur(10px);
}

.filter-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.filter-card__eyebrow,
.section-heading__eyebrow {
  color: #2a8a43;
}

.filter-card__header h2 {
  margin: 0.45rem 0 0;
  color: #173425;
  font-size: 1.15rem;
  line-height: 1.35;
}

.filter-card__toggle {
  border: 0;
  border-radius: 14px;
  background: linear-gradient(135deg, #1f6f33, #2fa049);
  box-shadow: 0 12px 24px rgba(48, 171, 76, 0.14);
  white-space: nowrap;
}

.filter-card__collapse {
  margin-top: 1.2rem;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  align-items: end;
}

.filter-field label {
  display: block;
  margin-bottom: 0.45rem;
  color: #486353;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.filter-field ::v-deep .custom-select {
  min-height: 3.1rem;
  border: 1px solid rgba(28, 68, 38, 0.12);
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(243, 248, 244, 0.92));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 10px 20px rgba(18, 42, 24, 0.04);
  color: #1f3225;
}

.filter-total {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100%;
  padding: 1rem;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(247, 251, 248, 0.96), rgba(239, 246, 241, 0.96));
  border: 1px solid rgba(24, 57, 31, 0.08);
}

.filter-total__label {
  margin-bottom: 0.35rem;
  color: #688173;
}

.value {
  font-size: clamp(1.6rem, 3vw, 2rem);
  color: #1d6f37;
}

.spinner {
  color: #1d6f37;
  z-index: 200;
}

.analysis-section {
  padding: 0 0.75rem;
}

.section-heading {
  margin-bottom: 1.35rem;
}

.analysis-grid {
  margin-top: 0.25rem;
}

.analysis-divider {
  max-width: 1240px;
  height: 1px;
  margin: 0 auto 2rem;
  background: linear-gradient(
    90deg,
    rgba(42, 138, 67, 0),
    rgba(42, 138, 67, 0.18),
    rgba(42, 138, 67, 0)
  );
}

.analysis-icon {
  filter: drop-shadow(0 10px 20px rgba(48, 171, 76, 0.12));
}

.analysis-page ::v-deep .landing-card {
  min-height: 100%;
}

.analysis-page ::v-deep .landing-card__subtitle {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 3rem;
}

@media (max-width: 991px) {
  .analysis-page {
    padding-top: 1rem;
  }

  .analysis-hero,
  .filter-shell {
    margin-right: 1rem;
    margin-left: 1rem;
  }

  .analysis-hero {
    flex-direction: column;
    padding: 1.5rem;
  }

  .analysis-hero__summary,
  .filter-shell {
    min-width: 0;
    grid-template-columns: 1fr;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .analysis-page {
    padding-bottom: 2rem;
  }

  .analysis-hero {
    padding: 1.15rem;
    border-radius: 22px;
  }

  .analysis-hero h1 {
    font-size: 1.7rem;
  }

  .analysis-hero p {
    line-height: 1.6;
  }

  .filter-card {
    padding: 1rem;
    border-radius: 20px;
  }

  .filter-card__header {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-card__toggle {
    width: 100%;
  }

  .analysis-section {
    padding: 0 0.35rem;
  }
}

@media (max-width: 575px) {
  .analysis-hero,
  .filter-shell {
    margin-right: 0.75rem;
    margin-left: 0.75rem;
  }

  .analysis-page ::v-deep .landing-card {
    min-height: 18rem;
  }

  .analysis-page ::v-deep .landing-card__content {
    padding: 1.1rem;
  }
}
</style>
